'use client';
import React, { useEffect, useRef, useState } from 'react';
import Modal from '../Modal';
import useModal from '../../hooks/useModal';
import { useUserContext } from '../../context/UserContext';
import LockoutModalContent from '../LockoutModalContent';
import {
  ANSWERS_LOCKOUT_MODAL_LOCAL_STORAGE_KEY,
  LOCKOUT_MODAL_TIMEOUT,
} from '../../constants/lockout';
import { useAIPanelContext } from '../../context/AIPanelContext';
import type { TrialCtaArgs } from '../../lib/composer/schemas/answersValueProp';

const useInitialFocus = (
  isVisible: boolean,
  ref: React.RefObject<HTMLDivElement | null>,
) => {
  useEffect(() => {
    if (!isVisible) return;

    ref.current?.focus();
  }, [isVisible, ref]);
};

/**
 * LockoutModal is used to show a modal to the user after 30 seconds of viewing a page.
 */
const LockoutModal = (props: { nodeID: number; trialCta?: TrialCtaArgs }) => {
  const { nodeID, trialCta } = props;
  const modal = useModal();
  const { user, isUserLoaded } = useUserContext();
  const { isAILockoutOpen } = useAIPanelContext();
  const [pendingShow, setPendingShow] = useState(false);
  const isAILockoutOpenRef = useRef(false);
  const initialFocusRef = useRef<HTMLDivElement>(null);
  useInitialFocus(modal.isVisible, initialFocusRef);

  useEffect(() => {
    isAILockoutOpenRef.current = isAILockoutOpen;
  }, [isAILockoutOpen]);

  // When the OAI lockout closes, show the deferred timer lockout.
  useEffect(() => {
    if (!isAILockoutOpen && pendingShow) {
      modal.show();
      setPendingShow(false);
    }
  }, [isAILockoutOpen, pendingShow]);

  useEffect(() => {
    if (user || !isUserLoaded) return;

    const triggerShow = () => {
      if (isAILockoutOpenRef.current) {
        setPendingShow(true);
      } else {
        modal.show();
      }
    };

    const localStorageItem = JSON.parse(
      localStorage.getItem(ANSWERS_LOCKOUT_MODAL_LOCAL_STORAGE_KEY) || '{}',
    );

    // Show modal if the 30s has passed or the user has already visited the page.
    if (
      localStorageItem.nodeID === nodeID &&
      (Date.now() - localStorageItem.time > LOCKOUT_MODAL_TIMEOUT ||
        (localStorageItem.nodeID === nodeID && localStorageItem.visited))
    ) {
      triggerShow();
      return;
    }

    const timeout = setTimeout(() => {
      triggerShow();
      localStorage.setItem(
        ANSWERS_LOCKOUT_MODAL_LOCAL_STORAGE_KEY,
        JSON.stringify({ nodeID, time: Date.now().toString(), visited: true }),
      );
    }, LOCKOUT_MODAL_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [user, nodeID]);

  return (
    <Modal
      {...modal}
      hasHeader
      overlayOpacity={0.2}
      className="rounded-lg"
      _className="!overflow-visible !max-h-[85vh] !max-w-[600px] !w-full"
      hideIconClasses="opacity-100 absolute z-10 top-4 right-4 text-[16px] bg-transparent hover:bg-transparent"
      headerBackground="bg-white relative p-0"
    >
      <div
        ref={initialFocusRef}
        tabIndex={-1}
        data-testid="answers-lockout-modal-initial-focus"
      >
        <LockoutModalContent
          dataTestId="answers-lockout-modal"
          trialCta={trialCta}
        />
      </div>
    </Modal>
  );
};

export default LockoutModal;
